/** Company-only transport; backend owns authority, original terms and provider pins. */
import { httpClient } from "./client";
import type { CompanySoftwarePurchaseApi, CompanySoftwareRenewalControlApi, CompanySoftwareReceiptApi, CompanySoftwareExpiryApi } from "./companyPurchaseContracts";
import { canonicalModuleOfferLocator } from "./moduleOfferResponse";
import { readExpiryReview, readExpiryResult, requireExpiryRequest } from "./companyExpiryResponse";
import type {
  CommercialSoftwareMethodConsent,
  CommercialSoftwareMethodConsentGrantRequest,
  CommercialSoftwareMethodConsentRevokeRequest,
  CommercialSoftwareMethodDisclosureView,
  OrganizationSoftwareBillingConfirmationRequest,
  OrganizationSoftwareBillingConfirmationResult,
  OrganizationSoftwareBillingSetupRequest,
  OrganizationSoftwareBillingSetupResult,
  OrganizationSoftwareBillingView,
  OrganizationSoftwareMethodSetupPage,
  OrganizationSoftwareMethodSetupQuery,
  OrganizationSoftwareMethodSetupRequest,
  OrganizationSoftwareMethodSetupResult,
  OrganizationSoftwareMethodView,
} from "../types/generated";

export type CompanySoftwareBillingEnvironment =
  OrganizationSoftwareBillingView["environment"];

/**
 * Actor, request provenance, provider choices and pricing are never client inputs.
 * Raw setup results contain transient challenge material: do not cache/persist them.
 */
export interface CompanySoftwareBillingApi extends CompanySoftwarePurchaseApi, CompanySoftwareRenewalControlApi, CompanySoftwareReceiptApi, CompanySoftwareExpiryApi {
  getBilling(
    orgId: string,
    environment: CompanySoftwareBillingEnvironment,
  ): Promise<OrganizationSoftwareBillingView>;
  setupBilling(
    orgId: string,
    request: OrganizationSoftwareBillingSetupRequest,
  ): Promise<OrganizationSoftwareBillingSetupResult>;
  resumeCustomer(
    orgId: string,
    operationId: string,
    environment: CompanySoftwareBillingEnvironment,
  ): Promise<OrganizationSoftwareBillingView>;
  confirmBilling(
    orgId: string,
    request: OrganizationSoftwareBillingConfirmationRequest,
  ): Promise<OrganizationSoftwareBillingConfirmationResult>;
  getDisclosure(
    orgId: string,
    acceptanceId: string,
    environment: CompanySoftwareBillingEnvironment,
  ): Promise<CommercialSoftwareMethodDisclosureView>;
  grantConsent(
    orgId: string,
    request: CommercialSoftwareMethodConsentGrantRequest,
  ): Promise<CommercialSoftwareMethodConsent>;
  revokeConsent(
    orgId: string,
    request: CommercialSoftwareMethodConsentRevokeRequest,
  ): Promise<CommercialSoftwareMethodConsent>;
  getMethod(
    orgId: string,
    acceptanceId: string,
    environment: CompanySoftwareBillingEnvironment,
  ): Promise<OrganizationSoftwareMethodView>;
  setupMethod(
    orgId: string,
    request: OrganizationSoftwareMethodSetupRequest,
  ): Promise<OrganizationSoftwareMethodSetupResult>;
  resumeMethod(
    orgId: string,
    operationId: string,
  ): Promise<OrganizationSoftwareMethodSetupResult>;
  listMethodSetups(
    orgId: string,
    acceptanceId: string,
    query: OrganizationSoftwareMethodSetupQuery,
  ): Promise<OrganizationSoftwareMethodSetupPage>;
}

/** Browser isolation only, never a request actor or a permission claim. */
export interface CompanySoftwareBillingScope {
  readonly actorId: string;
  readonly orgId: OrganizationSoftwareBillingView["org_id"];
  readonly acceptanceId: OrganizationSoftwareMethodSetupRequest["acceptance_id"];
  readonly environment: CompanySoftwareBillingEnvironment;
}

export type CompanyMethodCommandIntent = Readonly<{
  version: 1;
  scope: CompanySoftwareBillingScope;
} & (
  | { kind: "grant"; request: CommercialSoftwareMethodConsentGrantRequest }
  | { kind: "setup"; request: OrganizationSoftwareMethodSetupRequest }
  | { kind: "revoke"; request: CommercialSoftwareMethodConsentRevokeRequest }
)>;

const base = (org: string) => `/orgs/${encodeURIComponent(org)}/commercial`;
const agreement = (org: string, acceptance: string) => `${base(org)}/acceptances/${encodeURIComponent(acceptance)}`;
const environmentQuery = (environment: CompanySoftwareBillingEnvironment) => `?environment=${encodeURIComponent(environment)}`;

export const companySoftwareBillingApi: CompanySoftwareBillingApi = {
  getExpiryReview: async (org, purchase, environment) => {
    const endpoint = `${expiryPurchase(org, purchase)}/expiry-review${environmentQuery(environment)}`;
    if (environment !== "sandbox" && environment !== "production") throw new TypeError("An explicit environment is required.");
    return readExpiryReview(await httpClient.get<unknown>(endpoint), endpoint, org, purchase, environment);
  },
  completeExpiry: async (org, purchase, request) => {
    const endpoint = `${expiryPurchase(org, purchase)}/expiry`;
    requireExpiryRequest(request, purchase);
    const captured = Object.freeze({ ...request });
    return readExpiryResult(await httpClient.post<unknown>(endpoint, captured), endpoint, org, purchase, captured);
  },
  listReceipts: (org, purchase, environment, page) => httpClient.get(`${base(org)}/purchases/${encodeURIComponent(purchase)}/receipts${purchasePageQuery(environment, page)}`),
  getReceipt: (org, purchase, receipt, environment) => httpClient.get(`${base(org)}/purchases/${encodeURIComponent(purchase)}/receipts/${encodeURIComponent(receipt)}${environmentQuery(environment)}`),
  getPurchaseReview: (org, acceptance, environment) => httpClient.get(`${agreement(org, acceptance)}/purchase-review${environmentQuery(environment)}`),
  listPurchases: (org, acceptance, environment, page) => httpClient.get(`${agreement(org, acceptance)}/purchases${purchasePageQuery(environment, page)}`),
  purchaseModule: (org, request) => httpClient.post(`${base(org)}/purchases`, request),
  getPurchase: (org, purchase, environment) => httpClient.get(`${base(org)}/purchases/${encodeURIComponent(purchase)}${environmentQuery(environment)}`),
  resumePurchase: (org, purchase, environment) => httpClient.post(`${base(org)}/purchases/${encodeURIComponent(purchase)}/resume${environmentQuery(environment)}`),
  listInvoices: (org, purchase, environment, page) => httpClient.get(`${base(org)}/purchases/${encodeURIComponent(purchase)}/invoices${purchasePageQuery(environment, page)}`),
  stopRenewal: (org, purchase, request) => httpClient.post(`${base(org)}/purchases/${encodeURIComponent(purchase)}/stop-renewal`, request),
  restoreRenewal: (org, purchase, request) => httpClient.post(`${base(org)}/purchases/${encodeURIComponent(purchase)}/restore-renewal`, request),
  listRenewalControls: (org, purchase, environment, page) => httpClient.get(`${base(org)}/purchases/${encodeURIComponent(purchase)}/renewal-controls${purchasePageQuery(environment, page)}`),
  resumeRenewalControl: (org, purchase, operation, environment) => httpClient.post(`${base(org)}/purchases/${encodeURIComponent(purchase)}/renewal-controls/${encodeURIComponent(operation)}/resume${environmentQuery(environment)}`),
  getBilling: (org, environment) => httpClient.get(`${base(org)}/billing${environmentQuery(environment)}`),
  setupBilling: (org, request) => httpClient.post(`${base(org)}/billing/setup`, request),
  resumeCustomer: (org, operation, environment) => httpClient.post(`${base(org)}/billing/customer-setups/${encodeURIComponent(operation)}/resume${environmentQuery(environment)}`),
  confirmBilling: (org, request) => httpClient.post(`${base(org)}/billing/confirm`, request),
  getDisclosure: (org, acceptance, environment) => httpClient.get(`${agreement(org, acceptance)}/method-disclosure${environmentQuery(environment)}`),
  grantConsent: (org, request) => httpClient.post(`${base(org)}/billing/method-consents`, request),
  revokeConsent: (org, request) => httpClient.post(`${base(org)}/billing/method-consents/revoke`, request),
  getMethod: (org, acceptance, environment) => httpClient.get(`${agreement(org, acceptance)}/billing-method${environmentQuery(environment)}`),
  setupMethod: (org, request) => httpClient.post(`${base(org)}/billing/method-setups`, request),
  resumeMethod: (org, operation) => httpClient.post(`${base(org)}/billing/method-setups/${encodeURIComponent(operation)}/resume`),
  listMethodSetups: (org, acceptance, query) => {
    const params = new URLSearchParams({ environment: query.environment, limit: String(query.limit), offset: String(query.offset) });
    return httpClient.get(`${agreement(org, acceptance)}/method-setups?${params}`);
  },
};

function expiryPurchase(org: string, purchase: string): string {
  const organization = canonicalModuleOfferLocator(org), original = canonicalModuleOfferLocator(purchase);
  if (organization === null || original === null) throw new TypeError("An exact organization and original purchase are required.");
  return `${base(organization)}/purchases/${encodeURIComponent(original)}`;
}

function purchasePageQuery(environment: CompanySoftwareBillingEnvironment, page: { readonly limit: number; readonly offset: number }): string {
  return `?${new URLSearchParams({ environment, limit: String(page.limit), offset: String(page.offset) })}`;
}
